import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgbModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { InventoryService } from '../../_service/inventory.service';
import { debounceTime, Subject } from 'rxjs';

interface InventoryLog {
  id: number;
  receipt: string;
  inventory: {
    id: number;
    name: string;
    logs: any;
    selling_price: any;
    main_image: string | null;
    category?: {
      name: string;
    };
  };
  quantity: number;
  expiry_date: string;
  cost_price: number;
  selling_price: number;
  total_cost: number;
  is_expired: boolean;
  sold_out: boolean;
}

@Component({
  selector: 'app-inventory-auditory-logs',
  standalone: true,
  imports: [RouterModule, CommonModule, NgbModule, FormsModule],
  templateUrl: './inventory-auditory-logs.component.html',
  styleUrl: './inventory-auditory-logs.component.scss'
})
export class InventoryAuditoryLogsComponent implements OnInit {
  private invetorySrvs = inject(InventoryService);
  private modal = inject(NgbModal);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private searchSubject = new Subject<string>();

  // Data properties
  logs: InventoryLog[] = [];
  filteredLogs: InventoryLog[] = [];
  page = 1;
  size = 0; // Original data size for API pagination
  filteredSize = 0; // Filtered data size for display
  pageSize = 15;
  updatingQuantity = 0;
  selectedLog: InventoryLog | null = null;

  // Loading states
  isLoading = false;
  isUpdating: number | null = null;
  isUpdatingQuantity = false;
  isExporting = false;

  // Filter properties
  searchTerm = '';
  selectedStatus = '';
  dateRange = '';

  // Utility
  Math = Math;

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.listInventoryLogs();
  }

  private setupSearchDebounce(): void {
    this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
      this.applyFilters();
    });
  }

  listInventoryLogs() {
    const inventoryId = this.route.snapshot.paramMap.get('id');
    this.isLoading = true;

    this.invetorySrvs.listInventoriesLogs(this.page, inventoryId).subscribe({
      next: (res: any) => {
        this.logs = res.data;
        this.filteredLogs = [...this.logs];
        this.size = res.total; // Keep original size for pagination
        this.filteredSize = this.logs.length; // Set filtered size for display
        this.pageSize = res.per_page;
        this.applyFilters(); // Apply filters after data is loaded
        this.isLoading = false; // Set loading to false after all processing
      },
      error: (error) => {
        console.error('Error fetching logs:', error);
        this.isLoading = false;
        Swal.fire('Error', 'Failed to load inventory logs', 'error');
      }
    });
  }  refresh(page: number) {
    this.page = page;
    this.listInventoryLogs();
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/inventory']);
  }

  // Search and Filter Methods
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  applyFilters(): void {
    let filtered = [...this.logs];

    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        log.inventory.name.toLowerCase().includes(searchLower) ||
        log.inventory.category?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatus) {
      filtered = filtered.filter(log => {
        switch (this.selectedStatus) {
          case 'available':
            return !log.sold_out && !log.is_expired;
          case 'sold_out':
            return log.sold_out;
          case 'expired':
            return log.is_expired;
          default:
            return true;
        }
      });
    }

    // Apply date range filter
    if (this.dateRange) {
      const now = new Date();
      const filterDate = new Date();

      switch (this.dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          filterDate.setFullYear(1900);
      }

      filtered = filtered.filter(log =>
        new Date(log.expiry_date) >= filterDate
      );
    }

    this.filteredLogs = filtered;
    this.filteredSize = filtered.length; // Update filtered size instead of original size
    // Don't reset page when filtering - let user stay on current page if possible
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.dateRange = '';
    this.filteredLogs = [...this.logs];
    this.filteredSize = this.logs.length; // Update filtered size
    this.page = 1;
  }

  // Export functionality
  exportLogs(): void {
    this.isExporting = true;

    // Simulate export process
    setTimeout(() => {
      const csvContent = this.generateCSV();
      this.downloadCSV(csvContent, 'inventory-audit-logs.csv');
      this.isExporting = false;
      Swal.fire('Success', 'Audit logs exported successfully', 'success');
    }, 2000);
  }

  private generateCSV(): string {
    const headers = ['Item Name', 'Category', 'Quantity', 'Expiry Date', 'Cost Price', 'Selling Price', 'Total Value', 'Status'];
    const rows = this.filteredLogs.map(log => [
      log.inventory.name,
      log.inventory.category?.name || 'Uncategorized',
      log.quantity.toString(),
      new Date(log.expiry_date).toLocaleDateString(),
      log.cost_price.toString(),
      log.inventory.selling_price.toString(),
      log.total_cost.toString(),
      log.is_expired ? 'Expired' : (log.sold_out ? 'Sold Out' : 'Available')
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }

  private downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  // Utility methods
  getExpiryStatus(expiryDate: string): string {
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 3600 * 24));

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays <= 7) return `${diffDays} days left`;
    return 'Good condition';
  }

  toggleStatus(log: InventoryLog) {
    const newStatus = !log.sold_out;
    const statusText = newStatus ? 'sold out' : 'available';
    this.isUpdating = log.id;

    Swal.fire({
      title: 'Confirm Status Change',
      text: `Are you sure you want to mark this inventory as ${statusText}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, change it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.invetorySrvs.toggleInventoryStatus(log.id, { sold_out: newStatus }).subscribe({
          next: (res: any) => {
            Swal.fire('Success', `Inventory marked as ${statusText}`, 'success');
            this.listInventoryLogs();
            this.isUpdating = null;
          },
          error: (err) => {
            Swal.fire('Error', err.error.message || 'Failed to update status', 'error');
            this.isUpdating = null;
          }
        });
      } else {
        this.isUpdating = null;
      }
    });
  }

  view(receipt: any) {
    if (receipt) {
      window.open(receipt, '_blank');
    } else {
      Swal.fire('Info', 'No receipt available for this item', 'info');
    }
  }

  // Modal methods
  openUpdateModal(content: any, log: InventoryLog) {
    this.selectedLog = log;
    this.updatingQuantity = log.quantity;
    this.modal.open(content, {
      centered: true,
      size: 'md',
      backdrop: 'static'
    }).result.then(
      (result) => {
        if (result === 'save') {
          this.updateQuantity();
        }
      },
      (reason) => {
        // Modal dismissed
        this.selectedLog = null;
      }
    );
  }

  // Quantity management
  increaseQuantity(): void {
    this.updatingQuantity++;
  }

  decreaseQuantity(): void {
    if (this.updatingQuantity > 0) {
      this.updatingQuantity--;
    }
  }

  updateQuantity() {
    if (!this.selectedLog) return;

    this.isUpdatingQuantity = true;

    this.invetorySrvs.updateInventoryQuantity(this.selectedLog.id, this.updatingQuantity).subscribe({
      next: (res: any) => {
        Swal.fire('Success', 'Quantity updated successfully', 'success');
        this.listInventoryLogs();
        this.selectedLog = null;
        this.isUpdatingQuantity = false;
      },
      error: (err) => {
        Swal.fire('Error', err.error.message || 'Failed to update quantity', 'error');
        this.isUpdatingQuantity = false;
      }
    });
  }

  deleteLog(log: InventoryLog): void {
    Swal.fire({
      title: 'Delete Audit Log',
      text: 'Are you sure you want to delete this audit log? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        // Implement delete functionality when API is available
        Swal.fire('Info', 'Delete functionality will be implemented when API is available', 'info');
      }
    });
  }
}
